const API_DESCRIPTION_BLOCK_RE =
  /```api-description\s*\n([\s\S]*?)```/i;
const FENCED_BLOCK_RE = /```[\w-]*\s*\n[\s\S]*?```/g;

const SPEC_TRUNCATION_LIMIT = 80_000;

interface OpenApiSummary {
  info?: {
    title?: string;
    version?: string;
    description?: string;
  };
  paths?: Record<
    string,
    Record<string, { summary?: string; description?: string }>
  >;
}

function summarizeOpenApiSpec(specContent: string): string {
  try {
    const parsed = JSON.parse(specContent) as OpenApiSummary;
    const paths = parsed.paths ?? {};
    const operations: string[] = [];

    for (const [path, methods] of Object.entries(paths)) {
      for (const [method, operation] of Object.entries(methods)) {
        if (method.startsWith("x-") || typeof operation !== "object") continue;
        const summary =
          operation.summary || operation.description || "(no summary)";
        operations.push(`${method.toUpperCase()} ${path}: ${summary}`);
      }
    }

    return JSON.stringify(
      {
        info: parsed.info ?? {},
        pathCount: Object.keys(paths).length,
        operations: operations.slice(0, 100),
        note:
          operations.length > 100
            ? `Showing first 100 of ${operations.length} operations.`
            : undefined,
      },
      null,
      2,
    );
  } catch {
    return specContent.slice(0, SPEC_TRUNCATION_LIMIT);
  }
}

function formatSpecForPrompt(specContent: string | null | undefined): string {
  if (!specContent?.trim()) {
    return "No OpenAPI specification is available for this API.";
  }

  if (specContent.length <= SPEC_TRUNCATION_LIMIT) {
    return specContent;
  }

  return (
    "The full OpenAPI spec was too large to include. Here is a compact summary:\n\n" +
    summarizeOpenApiSpec(specContent)
  );
}

export function buildApiDescriptionEnhancePrompt(input: {
  name: string;
  version: string;
  uuid: string;
  description?: string | null;
  specContent?: string | null;
}): string {
  const currentDescription = input.description?.trim() || "(empty)";
  const specSection = formatSpecForPrompt(input.specContent);

  return (
    `Enhance the CMS API catalog description for API "${input.name}" ` +
    `(version ${input.version}, uuid ${input.uuid}).\n\n` +
    `Current CMS description:\n${currentDescription}\n\n` +
    `OpenAPI specification:\n${specSection}\n\n` +
    `Write an improved catalog description that summarizes what this API does for product managers and API consumers. ` +
    `Base it on the current description and the spec (paths, operations, schemas). ` +
    `Do NOT modify the OpenAPI spec.\n\n` +
    `Return exactly one fenced block:\n` +
    "```api-description\n" +
    '{ "description": "..." }\n' +
    "```"
  );
}

export function parseApiDescriptionEnhancement(
  response: string,
): string | null {
  const match = response.match(API_DESCRIPTION_BLOCK_RE);
  if (match?.[1]) {
    try {
      const parsed = JSON.parse(match[1].trim()) as { description?: string };
      if (typeof parsed.description === "string" && parsed.description.trim()) {
        return parsed.description.trim();
      }
    } catch {
      /* fall through to plain-text fallback */
    }
  }

  const withoutFences = response.replace(FENCED_BLOCK_RE, "").trim();
  return withoutFences || null;
}
